import type { DragItem, DragOptionData } from '../dnd';

export type ChipOnChange = (
  widgetId: string,
  value: string,
  fromDrop?: boolean,
  sourceOptionId?: string,
) => void;

/**
 * Apply a dropped item onto the input identified by `targetId`.
 *
 * - `drag-option`: a fresh chip from the bank → fill the target.
 * - `placed-chip`: a chip moved from another input/slot → clear its source slot,
 *   then fill the target. Dropping a chip back onto itself is a no-op.
 *
 * Returns true when the item was handled, false when ignored.
 */
export function applyDropToInput(targetId: string, item: DragItem, onChange: ChipOnChange): boolean {
  if (item.type === 'drag-option') {
    const data = item.data as DragOptionData;
    onChange(targetId, data.value, true, String(item.id));
    return true;
  }
  if (item.type === 'placed-chip') {
    if (item.widgetId === targetId) return false; // dropped back on itself — no-op
    onChange(item.widgetId, '', false); // clear the source slot
    onChange(targetId, item.label, true, item.sourceOptionId); // place it here
    return true;
  }
  return false;
}

/** Minimal shape of a dnd-kit drag-end event we care about for undo. */
interface DragEndLike {
  active: { id: string | number };
  over: { id: string | number } | null;
}

/**
 * True when this drag-end means "undo": the chip with `dragId` was released
 * over empty space (no drop target). Drops onto an input are handled by that
 * input's drop handler instead.
 */
export function isUndoDrop(event: DragEndLike, dragId: string): boolean {
  return event.active.id === dragId && !event.over;
}
